import { Injectable } from "@angular/core";
import { Observable, catchError, distinctUntilChanged, filter, forkJoin, lastValueFrom, map, of } from "rxjs";
import { Store } from "@ngxs/store";
import { ApiService, SignalingService, ErrorMessage, Emails, DraftEmailInsert, ReferralDraftEmail, SentEmail, UserState, HarloweSupport, User } from "@harlowe/core";
import { HubConnection } from "@microsoft/signalr";
import { environment } from "../../environments/environment";
import { ReferralClosureReason, TealCompReferral, TealCompReferralHeader, TealCompReferralUpdate } from "../models/teal-comp-referral";
import { Referrals } from "../state/referrals/referral-actions";
import { plainToClass } from "class-transformer";
import { FileUpload } from "./encounter.service";
import { Encounters } from "../state/encounters/encounter-actions";
import { MessageService } from "primeng/api";

export abstract class ReferralServiceBase {
    abstract receiveNewReferral(referral: TealCompReferralHeader): void;
    abstract getNewReferrals(): Promise<TealCompReferralHeader[]>
    abstract getActiveReferrals(): Promise<TealCompReferralHeader[]>
    abstract getReferral(referralId: number): Promise<TealCompReferral>
    abstract getReferrals(referralId: number[]): Promise<TealCompReferral[]>;
    abstract getRecentlyClosedReferrals(): Promise<TealCompReferralHeader[]>;
    abstract updateReferral(referralId: number, referral: TealCompReferralUpdate): Promise<TealCompReferralHeader | undefined>
    abstract submitForAuthorization(referralId: number): Promise<TealCompReferralHeader | undefined>
    abstract resubmitForAuthorization(referralId: number): Promise<TealCompReferralHeader | undefined>
    abstract placeReferralBackInReview(referralId: number): Promise<TealCompReferralHeader | undefined>;
    abstract addAttachments(referralId: number, files: FileUpload[]): Promise<HarloweSupport.File[]>;
    abstract deleteAttachment(referralId: number, fileId: number): Promise<HarloweSupport.File>;
    abstract getReferralClosureReasons(): Observable<ReferralClosureReason[]>;
    abstract closeReferral(referralId: number, closureReason: number): Promise<TealCompReferralHeader | undefined>
    abstract getContractPDF(contractId: number): Promise<Blob>;
    abstract createContract(referralId: number): Promise<TealCompReferralHeader>;
    abstract downloadContractPDF(contractExternalId: string): Promise<Blob>;
    abstract getReferralDraftEmails(): Promise<ReferralDraftEmail[]>;
    abstract getReferralDraftEmail(referralId: number, emailId: number): Promise<ReferralDraftEmail>;
    abstract sendNewEmail(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined>;
    abstract sendReferralDraftEmail(draft: ReferralDraftEmail): Promise<SentEmail | undefined>;
    abstract addReferralDraftEmail(referralId: number, email: ReferralDraftEmail): Promise<ReferralDraftEmail | undefined>

}

@Injectable({ providedIn: 'root' })
export class ReferralService extends ReferralServiceBase {

    override getReferralDraftEmail(referralId: number, emailId: number): Promise<ReferralDraftEmail> {
        return lastValueFrom(this._api.makeAPIGetCall<ReferralDraftEmail>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/emails/drafts/" + emailId.toString(), {})
            .pipe
            (
                map
                    (
                        draft => {
                            return plainToClass(ReferralDraftEmail, draft);
                        }
                    )
            ));
    }
    override getReferralDraftEmails(): Promise<ReferralDraftEmail[]> {
        return lastValueFrom(this._api.makeAPIGetCall<ReferralDraftEmail[]>(environment.harlowe.tealcomp.baseUri, "/referrals/emails/drafts", {})
            .pipe
            (
                map
                    (
                        drafts => drafts.map(draft => plainToClass(ReferralDraftEmail, draft))
                    )
            ));
    }
    override sendNewEmail(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealcomp.baseUri, "/referrals/" + encounterId.toString() + "/emails/send", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Sent' });
                                this._store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: encounterId, email: e }));
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })


                ));
    }
    override sendReferralDraftEmail(draft: ReferralDraftEmail): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealcomp.baseUri, "/referrals/" + draft.referralId.toString() + "/emails/drafts/" + draft.id?.toString() + "/send", {})
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Sent' });
                                this._store.dispatch(new Referrals.ReferralServiceReferralEmailSent({ referralId: draft.referralId, email: e }));
                                if (draft.id) this._store.dispatch(new Emails.EmailServiceEmailDraftSent({ draftEmailId: draft.id }))
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })


                ));
    }

    override addReferralDraftEmail(referralId: number, email: ReferralDraftEmail): Promise<ReferralDraftEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPutCall<ReferralDraftEmail>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/draft", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Saved! You can access it from the email sidebar.' });
                                this._store.dispatch(new Emails.EmailServiceReferralEmailDraftAdded({ draftEmail: e }));
                                return plainToClass(ReferralDraftEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Draft Email', detail: ' ' + error.message }); return of(undefined) })
                )
        )
    }

    //State observable for when current user is logged in
    currentUser$ = this._store.select(UserState.currentUser);
    private referralHub: HubConnection;

    constructor
        (
            private _store: Store,
            private _api: ApiService,
            private _signaling: SignalingService,
            private _messages: MessageService
        ) {

        super();
        this.referralHub = this._signaling.generateHub(environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.referrals);

        //set up our hub connection when the user is officinally logged in
        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe
            (cu => {
                if (cu?.id != null)
                {
                    //Try starting the hub, once we're logged in
                    this._signaling.startHub(this.referralHub);
                }
                else
                {
                    this.referralHub.stop().catch(err => console.error('Hub stop failed', err));
                }
            });
        //Handle inbound hub messages
        this.referralHub.on("NewReferralSubmitted", (referral: TealCompReferralHeader) => this.receiveNewReferral(referral));

        this.referralHub.on("ReferralClosed", (referral: TealCompReferralHeader) => _store.dispatch(new Referrals.ReferralClosed({ referral: referral })));

        //When a referral is updated
        this.referralHub.on("Referralupdated", (referral: TealCompReferralHeader) => _store.dispatch(new Referrals.ReferralUpdated({ referral: referral })));

        this.referralHub.on("ItemUpdated", (referral: TealCompReferralHeader) => _store.dispatch(new Referrals.ReferralUpdated({ referral: referral })));
    }


    override receiveNewReferral(referral: TealCompReferralHeader): void {
        this._store.dispatch(new Referrals.NewReferralReceived({ referral: referral }))
    }

    override getReferral(referralId: number): Promise<TealCompReferral> {
        return lastValueFrom(this._api.makeAPIGetCall<TealCompReferral>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString(), {})
            .pipe(map(r => plainToClass(TealCompReferral, r))));
    }

    override getReferrals(referralIds: number[]): Promise<TealCompReferral[]> {
        if (referralIds == null || referralIds.length == 0) {
            return Promise.resolve([]);
        }

        return lastValueFrom(this._api.makeAPIGetCall<TealCompReferral[]>(environment.harlowe.tealcomp.baseUri, "/referrals/list",
            {
                ...referralIds && { referralId: referralIds.map(r => r.toString()) }
            }
        )
            .pipe(map(r => r.map(rr => plainToClass(TealCompReferral, rr)))));
    }

    override getNewReferrals(): Promise<TealCompReferralHeader[]> {
        return lastValueFrom(this._api.makeAPIGetCall<TealCompReferralHeader[]>(environment.harlowe.tealcomp.baseUri, "/referrals", { statuses: "0" }, {}));
    }

    override getActiveReferrals(): Promise<TealCompReferralHeader[]> {
        return lastValueFrom(this._api.makeAPIGetCall<TealCompReferralHeader[]>(environment.harlowe.tealcomp.baseUri, "/referrals", { statuses: ["0", "1", "2"] }, {}));
    }

    override getRecentlyClosedReferrals(): Promise<TealCompReferralHeader[]> {
        return lastValueFrom(this._api.makeAPIGetCall<TealCompReferralHeader[]>(environment.harlowe.tealcomp.baseUri, "/referrals/closed", { statuses: "3" }, {}));
    }

    override updateReferral(referralId: number, referral: TealCompReferralUpdate): Promise<TealCompReferralHeader | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPatchCall<TealCompReferralHeader>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString(), referral)
                    .pipe
                    (
                        map
                            (
                                r => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Referral ' + referralId });
                                    this._store.dispatch(new Referrals.ReferralUpdated({ referral: r }));
                                    return r;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Failed to Update Referral', detail: error.message }); return of<undefined>() })
                    )
            );
    }

    override submitForAuthorization(referralId: number): Promise<TealCompReferralHeader | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompReferralHeader>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/submitauthorization", {})
                    .pipe
                    (
                        map
                            (
                                r => {
                                    this._messages.add({ key: "main-alert", severity: 'success', summary: 'Authorization Sent' });
                                    return r;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Failed To Submit Auth', detail: error.message }); return of<undefined>() })
                    )
            );
    }

    override placeReferralBackInReview(referralId: number): Promise<TealCompReferralHeader | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompReferralHeader>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/place_back_in_review", {})
                    .pipe
                    (
                        map
                            (
                                r => {
                                    this._messages.add({ key: "main-alert", severity: 'success', summary: 'Referral Place Back in Review' });
                                    return r;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Failed To Put Referral Back In Review', detail: error.message }); return of<undefined>() })
                    )
            )
    }

    override resubmitForAuthorization(referralId: number): Promise<TealCompReferralHeader | undefined> {

        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompReferralHeader>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/resubmitauth", {})
                    .pipe
                    (
                        map
                            (
                                r => {
                                    this._messages.add({ key: "main-alert", severity: 'success', summary: 'Authorization Re-Sent' });
                                    return r;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Failed To Resubmit Auth', detail: error.message }); return of<undefined>() })
                    )
            )
    }


    override addAttachments(referralId: number, files: FileUpload[]): Promise<HarloweSupport.File[]> {
        return lastValueFrom
            (
                forkJoin
                    (
                        files.map
                            (
                                f => {
                                    var d: FormData = new FormData();
                                    d.append('file', f);
                                    if (f.category)
                                        d.append('category', f.category)
                                    if (f.fileType)
                                        d.append('fileType', f.fileType)

                                    return this._api.makeAPIPutCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/attachments", d, {})
                                }
                            )
                    )
                    .pipe
                    (
                        map
                            (
                                f => {
                                    return f;
                                }
                            )
                    )
            )
    }

    override deleteAttachment(referralId: number, fileId: number): Promise<HarloweSupport.File> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/attachments/" + fileId.toString(), {})

                    .pipe
                    (
                        map
                            (
                                f => {
                                    return f;
                                }
                            )
                    )
            )
    }


    override getReferralClosureReasons(): Observable<ReferralClosureReason[]> {
        return this._api.makeAPIGetCall<ReferralClosureReason[]>(environment.harlowe.tealcomp.baseUri, "/referrals/closureReasons", {});
    }


    override closeReferral(referralId: number, closureReason: number): Promise<TealCompReferralHeader | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompReferralHeader>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/close", {},
                    {
                        ...closureReason && { closureReason: closureReason.toString() }
                    })
                    .pipe
                    (
                        map
                            (
                                r => {
                                    this._messages.add({ key: "main-alert", severity: 'success', summary: 'Referral Closed' });
                                    return r;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Close Referral', detail: error.message }); return of() })
                    )
            );
    }

    override getContractPDF(referralId: number) {
        return lastValueFrom(this._api.getFileBlob(environment.harlowe.tealcomp.baseUri, `/referrals/${referralId}/contract/download`, {}));
    }

    override createContract(referralId: number): Promise<TealCompReferralHeader> {
        return lastValueFrom(this._api.makeAPIPutCall<any>(environment.harlowe.tealcomp.baseUri, "/referrals/" + referralId.toString() + "/contract", {})
            .pipe
            (
                map
                    (
                        r => {
                            this._messages.add({ key: "main-alert", severity: 'success', summary: 'Authorization Created' });
                            this._store.dispatch(new Referrals.AuthorizationCreatedForReferral({ referral: r }));
                            return r;
                        }
                    ),
                catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Authorization', detail: error.message }); return of() })
            )
        );
    }

    override downloadContractPDF(contractExternalId: string) {
        return lastValueFrom(this._api.getFileBlob(environment.harlowe.tealcomp.baseUri, "/referrals/contract/" + contractExternalId + "/download", {}));
    }


}